import React, { useEffect, useState } from 'react';
import {
  Activity,
  AlertTriangle,
  Clock,
  Globe,
  Monitor,
  RefreshCw,
  Shield,
  TrendingDown,
  TrendingUp,
  Users,
} from 'lucide-react';
import {
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { toast } from 'sonner';

import DataTable from '@/components/DataTable';
import EmptyState from '@/components/EmptyState';
import ErrorBoundary from '@/components/ErrorBoundary';
import LoadingSkeleton from '@/components/LoadingSkeleton';
import OnboardingWizard from '@/components/OnboardingWizard';
import QuickSetupChecklist from '@/components/QuickSetupChecklist';
import RiskGauge from '@/components/RiskGauge';
import SeverityBadge from '@/components/SeverityBadge';
import StatCard from '@/components/StatCard';
import StatusBadge from '@/components/StatusBadge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import api from '@/lib/api';

const RANGE_OPTIONS = [
  { key: 'today', label: 'Today', days: 1 },
  { key: '7d', label: '7 days', days: 7 },
  { key: '30d', label: '30 days', days: 30 },
];

const CHART_COLORS = {
  critical: '#f43f5e',
  high: '#f59e0b',
  medium: '#3b82f6',
  low: '#8b5cf6',
  pie: ['#3b82f6', '#10b981', '#f59e0b', '#f43f5e', '#8b5cf6', '#64748b'],
};

const tooltipStyle = {
  backgroundColor: '#1e293b',
  border: '1px solid #334155',
  borderRadius: '12px',
  color: '#e2e8f0',
};

function getDateWindow(rangeKey) {
  const range = RANGE_OPTIONS.find((option) => option.key === rangeKey) || RANGE_OPTIONS[1];
  const end = new Date();
  end.setHours(23, 59, 59, 999);

  const start = new Date(end);
  start.setHours(0, 0, 0, 0);
  start.setDate(start.getDate() - (range.days - 1));

  return {
    days: range.days,
    startDate: start.toISOString().slice(0, 10),
    endDate: end.toISOString().slice(0, 10),
  };
}

function formatTimestamp(value) {
  if (!value) return 'Unknown';
  try {
    const date = new Date(value);
    if (isNaN(date.getTime())) return 'Invalid Date';
    return new Intl.DateTimeFormat(undefined, {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(date);
  } catch (err) {
    return 'Invalid Date';
  }
}

function formatDayLabel(value) {
  return new Intl.DateTimeFormat(undefined, {
    month: 'short',
    day: 'numeric',
  }).format(new Date(value));
}

function formatAlertType(value) {
  return String(value || 'unknown').replace(/_/g, ' ');
}

function getTopRiskFactor(factors = {}) {
  if (!factors) return { key: 'baseline', label: 'Stable baseline', score: 0 };
  
  const factorEntries = Object.entries(factors);

  if (!factorEntries.length) {
    return { key: 'baseline', label: 'Stable baseline', score: 0 };
  }

  const sorted = [...factorEntries].sort(
    (first, second) => (second[1]?.score || 0) - (first[1]?.score || 0)
  );
  
  if (!sorted.length || !sorted[0]) {
    return { key: 'baseline', label: 'Stable baseline', score: 0 };
  }

  const [factorKey, factorValue] = sorted[0];

  return {
    key: factorKey || 'generic',
    label: String(factorKey || 'generic').replace(/_/g, ' '),
    score: factorValue?.score || 0,
  };
}

function categorizeApps(apps = []) {
  const buckets = {
    Browsers: 0,
    Collaboration: 0,
    Engineering: 0,
    Productivity: 0,
    Design: 0,
    Other: 0,
  };

  apps.forEach((app) => {
    const name = String(app.name || '').toLowerCase();

    if (/chrome|edge|firefox|safari|browser/.test(name)) {
      buckets.Browsers += app.sec || 0;
      return;
    }

    if (/slack|teams|outlook|zoom|meet|discord/.test(name)) {
      buckets.Collaboration += app.sec || 0;
      return;
    }

    if (/code|cursor|terminal|intellij|pycharm|webstorm|xcode|vim/.test(name)) {
      buckets.Engineering += app.sec || 0;
      return;
    }

    if (/excel|word|powerpoint|notion|sheets|docs|figma/.test(name)) {
      buckets.Productivity += app.sec || 0;
      return;
    }

    if (/photoshop|illustrator|after effects|premiere/.test(name)) {
      buckets.Design += app.sec || 0;
      return;
    }

    buckets.Other += app.sec || 0;
  });

  return Object.entries(buckets)
    .filter(([, value]) => value > 0)
    .map(([name, value]) => ({ name, value }))
    .sort((first, second) => second.value - first.value);
}

function buildAlertTrends(alerts = [], days = 7) {
  const normalized = Array.from({ length: days }).map((_, index) => {
    const pointDate = new Date();
    pointDate.setHours(0, 0, 0, 0);
    pointDate.setDate(pointDate.getDate() - (days - index - 1));
    const isoDay = pointDate.toISOString().slice(0, 10);

    return {
      date: isoDay,
      label: formatDayLabel(isoDay),
      critical: 0,
      high: 0,
      medium: 0,
      low: 0,
    };
  });

  const pointMap = normalized.reduce((accumulator, item) => {
    accumulator[item.date] = item;
    return accumulator;
  }, {});

  alerts.forEach((alert) => {
    const dateKey = String(alert.ts || '').slice(0, 10);
    const severity = String(alert.severity || '').toLowerCase();

    if (pointMap[dateKey] && Object.prototype.hasOwnProperty.call(pointMap[dateKey], severity)) {
      pointMap[dateKey][severity] += 1;
    }
  });

  return normalized;
}

function DashboardContent() {
  const [range, setRange] = useState('7d');
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [alertPage, setAlertPage] = useState(1);
  const [data, setData] = useState({
    stats: null,
    activityDistribution: [],
    alertTrends: [],
    recentAlerts: [],
    recentAlertsTotal: 0,
    riskScores: [],
    averageRisk: 0,
  });

  const loadDashboard = async ({ silent = false } = {}) => {
    const { startDate, endDate, days } = getDateWindow(range);

    if (silent) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }

    const requests = await Promise.allSettled([
      api.get('/dashboard/stats'),
      api.get('/employees', { params: { page: 1, page_size: 1 } }),
      api.get('/alerts', { params: { status: 'open', page: 1, page_size: 1 } }),
      api.get('/alerts', { params: { page: alertPage, page_size: 10 } }),
      api.get('/alerts', { params: { page: 1, page_size: 200 } }),
      api.get('/analytics/productivity', { params: { start_date: startDate, end_date: endDate } }),
      api.get('/analytics/risk-scores'),
    ]);

    const [
      statsResult,
      employeesResult,
      openAlertsResult,
      recentAlertsResult,
      allAlertsResult,
      productivityResult,
      riskScoresResult,
    ] = requests;

    const failed = requests.filter((result) => result.status === 'rejected');
    if (failed.length) {
      toast.error('Some dashboard panels could not be loaded.');
    }

    const stats = statsResult.status === 'fulfilled' ? statsResult.value.data : null;
    const employeesTotal =
      employeesResult.status === 'fulfilled' ? employeesResult.value.data?.total || 0 : 0;
    const openAlertsTotal =
      openAlertsResult.status === 'fulfilled' ? openAlertsResult.value.data?.total || 0 : 0;
    const recentAlerts =
      recentAlertsResult.status === 'fulfilled' ? recentAlertsResult.value.data?.items || [] : [];
    const recentAlertsTotal =
      recentAlertsResult.status === 'fulfilled' ? recentAlertsResult.value.data?.total || 0 : 0;
    const alertHistory =
      allAlertsResult.status === 'fulfilled' ? allAlertsResult.value.data?.items || [] : [];
    const productivity =
      productivityResult.status === 'fulfilled' ? productivityResult.value.data || {} : {};
    const riskScores =
      riskScoresResult.status === 'fulfilled' ? riskScoresResult.value.data?.items || [] : [];

    const filteredHistory = alertHistory.filter((alert) => {
      const ts = new Date(alert.ts);
      const { startDate: from } = getDateWindow(range);
      return ts >= new Date(from);
    });

    const averageRisk = riskScores.length
      ? riskScores.reduce((total, item) => total + (item.overall_risk || 0), 0) / riskScores.length
      : 0;

    setData({
      stats: {
        ...stats,
        total_employees: employeesTotal || stats?.total_employees || 0,
        open_alerts: openAlertsTotal || stats?.open_alerts || 0,
      },
      activityDistribution: categorizeApps(productivity.top_apps || []),
      alertTrends: buildAlertTrends(filteredHistory, days),
      recentAlerts,
      recentAlertsTotal,
      riskScores,
      averageRisk,
    });

    setLoading(false);
    setRefreshing(false);
  };

  useEffect(() => {
    let active = true;

    const run = async () => {
      await loadDashboard();
      if (!active) return;
    };

    run();

    return () => {
      active = false;
    };
  }, [range, alertPage]);

  const stats = data.stats || {};
  const totalEmployees = stats.total_employees || 0;
  const totalDevices = stats.total_devices || 0;
  const activeDevices = stats.online_devices || 0;
  const openAlerts = stats.open_alerts || 0;
  const criticalAlerts = stats.critical_alerts || 0;
  const averageRisk = data.averageRisk || 0;
  const topRiskEmployees = [...data.riskScores]
    .sort((first, second) => (second.overall_risk || 0) - (first.overall_risk || 0))
    .slice(0, 5);

  return (
    <div className="dashboard-mesh min-h-screen space-y-6 rounded-[28px] bg-slate-900 p-1 text-white">
      <OnboardingWizard />
      <div className="rounded-[24px] border border-slate-800 bg-slate-900/80 p-6 backdrop-blur">
        <div className="flex flex-col gap-4 xl:flex-row xl:items-center xl:justify-between">
          <div>
            <p className="text-sm uppercase tracking-[0.35em] text-slate-500">Executive View</p>
            <h1 className="mt-2 text-4xl font-semibold tracking-tight text-white">Dashboard</h1>
            <p className="mt-2 max-w-2xl text-sm text-slate-400">
              Live workforce telemetry, alert pressure, and behavioral risk in one demo-ready command
              center.
            </p>
          </div>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
            <div className="inline-flex rounded-xl border border-slate-700 bg-slate-800/80 p-1">
              {RANGE_OPTIONS.map((option) => (
                <button
                  key={option.key}
                  type="button"
                  className={`rounded-lg px-4 py-2 text-sm transition ${
                    range === option.key
                      ? 'bg-blue-500 text-white shadow-lg shadow-blue-500/20'
                      : 'text-slate-400 hover:text-slate-200'
                  }`}
                  onClick={() => setRange(option.key)}
                >
                  {option.label}
                </button>
              ))}
            </div>
            <Button
              className="border border-slate-700 bg-slate-800 text-slate-200 hover:bg-slate-700"
              onClick={() => loadDashboard({ silent: true })}
            >
              <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </div>

        <div className="mt-3 flex items-center gap-3 text-xs uppercase tracking-[0.28em] text-slate-500">
          <span className="inline-flex h-2.5 w-2.5 rounded-full bg-emerald-400 shadow-[0_0_18px_rgba(52,211,153,0.75)]" />
          Demo telemetry active
        </div>

        <div className="mt-6">
          <QuickSetupChecklist />
        </div>

        <div className="mt-6">
          {loading && !data.stats ? (
            <LoadingSkeleton type="card" />
          ) : (
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
              <StatCard
                title="Total Employees"
                value={totalEmployees}
                change={totalEmployees ? Math.min(100, (totalDevices / totalEmployees) * 100) : 0}
                changeLabel="device coverage"
                icon={<Users className="h-5 w-5" />}
                color="blue"
              />
              <StatCard
                title="Active Devices"
                value={activeDevices}
                change={totalDevices ? (activeDevices / totalDevices) * 100 : 0}
                changeLabel="online rate"
                icon={<Monitor className="h-5 w-5" />}
                color="green"
              />
              <StatCard
                title="Open Alerts"
                value={openAlerts}
                change={openAlerts ? -(criticalAlerts / openAlerts) * 100 : 0}
                changeLabel="critical share"
                icon={<AlertTriangle className="h-5 w-5" />}
                color="red"
              />
              <StatCard
                title="Avg Risk Score"
                value={data.riskScores.length ? averageRisk.toFixed(1) : '--'}
                change={60 - averageRisk}
                changeLabel="to high-risk threshold"
                icon={<Shield className="h-5 w-5" />}
                color="purple"
              />
            </div>
          )}
        </div>

        <div className="mt-6 grid grid-cols-1 gap-4 lg:grid-cols-2">
          <Card className="border-slate-700 bg-slate-800 shadow-lg">
            <CardHeader className="pb-0">
              <CardTitle className="flex items-center gap-2 text-white">
                <Activity className="h-5 w-5 text-blue-400" />
                Alert Trends
              </CardTitle>
              <p className="text-sm text-slate-500">
                Severity-by-severity alert flow over the selected time window.
              </p>
            </CardHeader>
            <CardContent className="pt-6">
              {loading && !data.alertTrends.length ? (
                <LoadingSkeleton type="chart" />
              ) : data.alertTrends.some((point) =>
                ['critical', 'high', 'medium', 'low'].some((key) => point[key] > 0)
              ) ? (
                <div className="h-[320px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={data.alertTrends}>
                      <CartesianGrid stroke="#334155" strokeDasharray="3 3" />
                      <XAxis dataKey="label" tick={{ fill: '#94a3b8', fontSize: 12 }} />
                      <YAxis tick={{ fill: '#94a3b8', fontSize: 12 }} />
                      <Tooltip contentStyle={tooltipStyle} />
                      <Legend wrapperStyle={{ color: '#cbd5e1' }} />
                      <Line type="monotone" dataKey="critical" stroke={CHART_COLORS.critical} strokeWidth={2.5} dot={false} />
                      <Line type="monotone" dataKey="high" stroke={CHART_COLORS.high} strokeWidth={2.5} dot={false} />
                      <Line type="monotone" dataKey="medium" stroke={CHART_COLORS.medium} strokeWidth={2.5} dot={false} />
                      <Line type="monotone" dataKey="low" stroke={CHART_COLORS.low} strokeWidth={2.5} dot={false} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <EmptyState
                  icon={<TrendingDown className="h-8 w-8" />}
                  title="No alert trend yet"
                  description="Recent alerts will appear here once the monitoring pipeline emits events."
                />
              )}
            </CardContent>
          </Card>

          <Card className="border-slate-700 bg-slate-800 shadow-lg">
            <CardHeader className="pb-0">
              <CardTitle className="flex items-center gap-2 text-white">
                <Clock className="h-5 w-5 text-emerald-400" />
                Activity Distribution
              </CardTitle>
              <p className="text-sm text-slate-500">
                Productive time inferred from top applications across the selected range.
              </p>
            </CardHeader>
            <CardContent className="pt-6">
              {loading && !data.activityDistribution.length ? (
                <LoadingSkeleton type="chart" />
              ) : data.activityDistribution.length ? (
                <div className="grid grid-cols-1 gap-6 xl:grid-cols-[minmax(0,1fr),220px] xl:items-center">
                  <div className="h-[320px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={data.activityDistribution}
                          dataKey="value"
                          nameKey="name"
                          innerRadius={80}
                          outerRadius={116}
                          paddingAngle={4}
                        >
                          {data.activityDistribution.map((entry, index) => (
                            <Cell
                              key={`${entry.name}-${index}`}
                              fill={CHART_COLORS.pie[index % CHART_COLORS.pie.length]}
                            />
                          ))}
                        </Pie>
                        <Tooltip
                          contentStyle={tooltipStyle}
                          formatter={(value) => [`${Math.round(value / 60)} min`, 'Time']}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                  <div className="space-y-3">
                    {data.activityDistribution.map((entry, index) => (
                      <div
                        key={entry.name}
                        className="rounded-xl border border-slate-700 bg-slate-900/70 p-3"
                      >
                        <div className="flex items-center justify-between gap-3">
                          <div className="flex items-center gap-3">
                            <span
                              className="h-3 w-3 rounded-full"
                              style={{ backgroundColor: CHART_COLORS.pie[index % CHART_COLORS.pie.length] }}
                            />
                            <span className="font-medium text-slate-200">{entry.name}</span>
                          </div>
                          <span className="text-sm text-slate-400">
                            {Math.round(entry.value / 3600)}h
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <EmptyState
                  icon={<Activity className="h-8 w-8" />}
                  title="No activity distribution"
                  description="Application usage data has not been collected for this date range yet."
                />
              )}
            </CardContent>
          </Card>
          <Card className="border-slate-700 bg-slate-800 shadow-lg">
            <CardHeader className="pb-0">
              <CardTitle className="flex items-center gap-2 text-white">
                <Globe className="h-5 w-5 text-emerald-400" />
                Web Activity
              </CardTitle>
              <p className="text-sm text-slate-500">
                Top domains visited across the organization today.
              </p>
            </CardHeader>
            <CardContent className="pt-6">
              {loading && !stats.top_domains?.length ? (
                <LoadingSkeleton type="chart" />
              ) : stats.top_domains?.length ? (
                <div className="grid grid-cols-1 gap-6 xl:grid-cols-[minmax(0,1fr),220px] xl:items-center">
                  <div className="h-[320px]">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={stats.top_domains.map(d => ({ name: d.domain, value: d.sec }))}
                          dataKey="value"
                          nameKey="name"
                          innerRadius={80}
                          outerRadius={116}
                          paddingAngle={4}
                        >
                          {stats.top_domains.map((entry, index) => (
                            <Cell
                              key={`${entry.domain}-${index}`}
                              fill={CHART_COLORS.pie[index % CHART_COLORS.pie.length]}
                            />
                          ))}
                        </Pie>
                        <Tooltip
                          contentStyle={tooltipStyle}
                          formatter={(value) => [`${Math.round(value / 60)} min`, 'Time']}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                  <div className="space-y-3">
                    {stats.top_domains.slice(0, 5).map((entry, index) => (
                      <div
                        key={entry.domain}
                        className="rounded-xl border border-slate-700 bg-slate-900/70 p-3"
                      >
                        <div className="flex items-center justify-between gap-3">
                          <div className="flex items-center gap-3">
                            <span
                              className="h-3 w-3 rounded-full"
                              style={{ backgroundColor: CHART_COLORS.pie[index % CHART_COLORS.pie.length] }}
                            />
                            <span className="font-medium text-slate-200 truncate max-w-[120px]">{entry.domain}</span>
                          </div>
                          <span className="text-sm text-slate-400">
                            {Math.round(entry.sec / 60)}m
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <EmptyState
                  icon={<Globe className="h-8 w-8" />}
                  title="No web activity"
                  description="Domain tracking has not surfaced any data for today yet."
                />
              )}
            </CardContent>
          </Card>
        </div>


        <div className="mt-6 grid grid-cols-1 gap-4 lg:grid-cols-3">
          <div className="lg:col-span-2">
            <Card className="border-slate-700 bg-slate-800 shadow-lg">
              <CardHeader className="pb-0">
                <CardTitle className="flex items-center gap-2 text-white">
                  <AlertTriangle className="h-5 w-5 text-rose-400" />
                  Recent Alerts
                </CardTitle>
                <p className="text-sm text-slate-500">Latest alert activity across the organization.</p>
              </CardHeader>
              <CardContent className="pt-6">
                <DataTable
                  loading={loading && !data.recentAlerts.length}
                  data={data.recentAlerts}
                  emptyMessage="No alerts have been generated for this organization yet."
                  columns={[
                    {
                      key: 'severity',
                      label: 'Severity',
                      render: (value) => <SeverityBadge severity={value} />,
                    },
                    {
                      key: 'employee_name',
                      label: 'Employee',
                      render: (value) => value || 'System',
                    },
                    {
                      key: 'type',
                      label: 'Type',
                      render: (value) => (
                        <span className="capitalize text-slate-200">{formatAlertType(value)}</span>
                      ),
                    },
                    {
                      key: 'status',
                      label: 'Status',
                      render: (value) => <StatusBadge status={value} />,
                    },
                    {
                      key: 'ts',
                      label: 'Timestamp',
                      render: (value) => formatTimestamp(value),
                    },
                  ]}
                  pagination={{
                    page: alertPage,
                    pageSize: 10,
                    total: data.recentAlertsTotal,
                    onPageChange: setAlertPage,
                  }}
                />
              </CardContent>
            </Card>
          </div>

          <Card className="border-slate-700 bg-slate-800 shadow-lg">
            <CardHeader className="pb-0">
              <CardTitle className="flex items-center gap-2 text-white">
                <TrendingUp className="h-5 w-5 text-purple-400" />
                Top Risk Employees
              </CardTitle>
              <p className="text-sm text-slate-500">Highest behavioral exposure right now.</p>
            </CardHeader>
            <CardContent className="pt-6">
              {loading && !topRiskEmployees.length ? (
                <LoadingSkeleton type="text" count={5} />
              ) : topRiskEmployees.length ? (
                <div className="space-y-3">
                  {topRiskEmployees.map((employee, index) => {
                    const topFactor = getTopRiskFactor(employee.factors);

                    return (
                      <div
                        key={employee.employee_id}
                        className="flex items-center gap-3 rounded-2xl border border-slate-700 bg-slate-900/80 p-3"
                      >
                        <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-slate-800 text-sm font-semibold text-slate-300">
                          {index + 1}
                        </div>
                        <RiskGauge score={employee.overall_risk} size="sm" />
                        <div className="min-w-0 flex-1">
                          <p className="truncate font-medium text-white">{employee.employee_name}</p>
                          <p className="mt-1 truncate text-xs capitalize text-slate-500">
                            {topFactor.label} spike
                          </p>
                        </div>
                        <SeverityBadge severity={employee.risk_level === 'critical' ? 'critical' : employee.risk_level === 'high' ? 'high' : employee.risk_level === 'medium' ? 'medium' : 'low'} />
                      </div>
                    );
                  })}
                </div>
              ) : (
                <EmptyState
                  icon={<Shield className="h-8 w-8" />}
                  title="No risk scores"
                  description="Risk scoring results will appear once UEBA calculations have been completed."
                />
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}

export default function Dashboard() {
  return (
    <ErrorBoundary>
      <DashboardContent />
    </ErrorBoundary>
  );
}
